import {useState} from 'react'
import {useDialogStore} from '@ariakit/react'
import {AxiosError} from 'axios'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {usePermissions} from '@/features/auth/store/store'
import {ActivityReportError, ActivityReportSector} from '@/features/accesses/activity-report/types'
import {resolveActivityReportErrorLabel} from '@/features/accesses/activity-report/utils'
import {useGetActivityReportCounters} from '@/features/accesses/activity-report/queries/useGetActivityReportCounters'
import {useStoreActivityReport} from '@/features/accesses/activity-report/queries/useStoreActivityReport'
import {ActivityReportConfigStep, ActivityReportPeriod} from './ActivityReportConfigStep'
import {ActivityReportResultStep} from './ActivityReportResultStep'

export const ActivityReportDialog = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({open: isOpen, setOpen: setIsOpen})

    if (!permissions?.projects?.write) {
        return null
    }

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    <Button variant="primaryNeutral" onClick={() => setIsOpen(true)}>
                        <PlusIcon />
                        {t('activity_report:new_report')}
                    </Button>
                }
            />
            {isOpen && <ActivityReportDialogShell onClose={() => dialogStore.setOpen(false)} />}
        </DialogProvider>
    )
}

const ActivityReportDialogShell = ({onClose}: {onClose: () => void}) => {
    const {t} = useTranslation()
    const [step, setStep] = useState<'config' | 'result'>('config')
    const [period, setPeriod] = useState<ActivityReportPeriod | null>(null)
    const [sector, setSector] = useState<ActivityReportSector | null>(null)

    const bothSelected = !!period && !!sector

    const countersQuery = useGetActivityReportCounters(
        {startDate: period?.startDate ?? '', endDate: period?.endDate ?? '', sector: sector ?? 'outpatient'},
        {enabled: bothSelected}
    )

    const mutation = useStoreActivityReport({
        onSuccess: () => setStep('result'),
        onError: (error: AxiosError<ActivityReportError>) => {
            toast.error(t(resolveActivityReportErrorLabel(error.response?.data?.code)))
        }
    })

    const handleGenerate = () => {
        if (!period || !sector) return
        mutation.mutate({startDate: period.startDate, endDate: period.endDate, sector})
    }

    return (
        <Dialog width="560px">
            <DialogHeader>
                <DialogHeading>{t('activity_report:dialog_title')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            {step === 'config' && (
                <ActivityReportConfigStep
                    period={period}
                    sector={sector}
                    onPeriodApply={setPeriod}
                    onSectorChange={setSector}
                    counters={countersQuery.data}
                    isCountersLoading={bothSelected && countersQuery.isLoading}
                    isCountersError={bothSelected && countersQuery.isError}
                    isGenerating={mutation.isPending}
                    onGenerate={handleGenerate}
                />
            )}
            {step === 'result' && (
                <ActivityReportResultStep
                    variant="success"
                    title={t('activity_report:success_title')}
                    subtitle={t('activity_report:success_subtitle')}
                    onClose={onClose}
                />
            )}
        </Dialog>
    )
}
